import type { DocSection, JdDraft } from '../../types'
import { FAMILIES } from './seed'

/**
 * Stands in for the section reader (FR-42): the document's own headings and
 * the lines under each.
 *
 * Reads blocks separated by a blank line. A block's first line is its
 * heading when more lines follow it and it reads like one: it does not end
 * a sentence, or it names itself before a colon. A closing statement is a
 * block of sentences and so has no heading, as the real reader leaves it.
 * Text written as one block has no sections, the way a failed section read
 * leaves an upload, so the form falls back to sections built from the fields.
 */
export function sectionsFromText(text: string): DocSection[] | undefined {
  const blocks = text.split(/\r?\n\s*\r?\n/)
    .map((b) => b.split(/\r?\n/).map((l) => l.trim().replace(/^[-•*]\s+/, '')).filter(Boolean))
    .filter((b) => b.length > 0)
  if (blocks.length < 2) return undefined
  return blocks.map((lines) => {
    const first = lines[0]!
    const heading = lines.length > 1 && (!/[.!?]$/.test(first) || /^[^:]{3,80}:/.test(first))
    return heading ? { heading: first, lines: lines.slice(1) } : { heading: '', lines }
  })
}

/**
 * Stands in for reading the hidden fields again from edited sections
 * (FR-50). The real worker hands the sections, as text, to the same reader
 * an upload uses; this files each section's lines by what its heading says,
 * which is enough for the screens to show the read happening.
 */
export function readFromSections(sections: DocSection[]): Record<string, string> {
  const under = (re: RegExp, join: string) =>
    sections.filter((s) => re.test(s.heading)).flatMap((s) => s.lines).join(join)
  return {
    summary: under(/summary|overview|purpose|about the role/i, ' '),
    responsibilities: under(/responsib|duties|what you will do/i, '\n'),
    skills: under(/skill|abilit|knowledge/i, ', '),
    quals: under(/qualif|education|experience|certif|requirement/i, '\n'),
    physicalDemands: under(/physical/i, '\n'),
    workEnvironment: under(/environment|working conditions/i, '\n'),
    totalRewards: under(/benefit|reward/i, '\n'),
    compensationNote: under(/compensation|pay/i, '\n'),
    legal: under(/legal|equal opportunity/i, '\n'),
  }
}

/**
 * How many job descriptions this text holds.
 *
 * The real reader is asked outright and has read the whole document. This has
 * to guess from shape, so it counts the markers that reliably start a new role
 * in a written set: a "Job Description:" or "Job Title:" heading, or a repeated
 * SUMMARY section. Undercounting is the safe direction, because the cost of
 * being wrong is refusing a file that would have read fine.
 */
export function rolesIn(text: string): number {
  const headings = text.match(/^\s*(job description|job title|position|role)\s*[:\-]/gim) ?? []
  const summaries = text.match(/^\s*(summary|job overview|role overview)\s*:?\s*$/gim) ?? []
  return Math.max(1, headings.length, summaries.length)
}

/**
 * Stands in for the Bedrock extraction while STE-544 is unbuilt. It fills the
 * same form the real call fills, so the review step is exercised for real: the
 * result is never saved without a person pressing save (FR-8).
 */
export function extractDraft(text: string): JdDraft {
  const pick = (re: RegExp) => {
    const m = text.match(re)
    return m ? m[1].trim() : ''
  }
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean)
  const bullets = lines.filter((l) => /^[-•*]/.test(l)).map((l) => l.replace(/^[-•*]\s*/, ''))
  const t = text.toLowerCase()

  const family = /perception|lidar|sensor fusion|calibration/.test(t)
    ? 'Perception'
    : /\bml\b|machine learning|pytorch|model training|inference/.test(t)
      ? 'Machine Learning'
      : /robot|motion planning|controls|state estimation|ros/.test(t)
        ? 'Robotics'
        : /pipeline|warehouse|etl|airflow|spark|lakehouse|data engineer/.test(t)
          ? 'Data Engineering'
          : 'Software Engineering'

  const level = /\bprincipal\b|\bdistinguished\b/.test(t)
    ? 'Principal'
    : /\bstaff\b/.test(t)
      ? 'Staff'
      : /\bsenior\b|\bsr\.?\b|\blead\b/.test(t)
        ? 'Senior'
        : /\bjunior\b|\bnew grad\b|\bentry\b/.test(t)
          ? 'Junior'
          : 'Mid'

  const known = [
    'PyTorch', 'TensorRT', 'C++17', 'ROS 2', 'Python', 'Spark', 'Airflow', 'dbt', 'Kafka',
    'Kubernetes', 'Sensor fusion', 'Distributed training', 'Model evaluation',
    'Real-time systems', 'State estimation', 'Control theory',
  ]
  const skillLine = text.match(/skills?\s*:\s*(.+)/i)
  const skills = skillLine
    ? skillLine[1].split(/[,;]/).map((x) => x.trim()).filter(Boolean)
    : known.filter((k) => t.includes(k.toLowerCase()))

  const pay = (text.match(/\$\s?\d[\d,]*/g) ?? [])
    .map((x) => parseInt(x.replace(/[^\d]/g, ''), 10))
    .map((v) => (v > 5000 ? v / 1000 : v))
    .filter((v) => v > 40 && v < 900)

  const quals = lines
    .filter((l) => /years|degree|bachelor|master|phd/i.test(l))
    .map((l) => l.replace(/^[-•*]\s*/, ''))
    .slice(0, 4)

  return {
    title: (pick(/title\s*:\s*(.+)/i) || lines[0] || 'Untitled role').replace(/^#+\s*/, '').slice(0, 90),
    family: FAMILIES.includes(family) ? family : 'Software Engineering',
    level,
    location: pick(/location\s*:\s*(.+)/i) || 'US national',
    // The stand-in reads no period out of pasted text, so it says a year,
    // which is what an unstated period means everywhere else.
    payUnit: 'year',
    payMin: pay.length ? String(Math.min(...pay)) : '',
    payMax: pay.length ? String(Math.max(...pay)) : '',
    summary:
      pick(/summary\s*:\s*(.+)/i) ||
      lines.find((l) => l.length > 70 && !/^[-•*]/.test(l)) ||
      '',
    responsibilities: bullets.slice(0, 8).join('\n'),
    skills: skills.join(', '),
    quals: quals.join('\n'),
    industry: '',
  }
}

export const SAMPLE_JD_TEXT = `Title: Staff Robotics Engineer
Level: Staff
Location: Detroit, MI
Summary: Owns the vehicle control and actuation stack for the next-generation platform, from bench prototype through fleet rollout.
- Own control, actuation and state estimation for the production platform.
- Lead the safety case for drive-by-wire changes.
- Mentor engineers across the Detroit and Bangalore teams.
Skills: C++17, ROS 2, Control theory, Drive-by-wire, Functional safety
Range: $230k - $275k`
